<template>
  <div class="room-extend-menu-section-contents">
    <RoomChatForm
      v-for="file in fileList"
      :key="file.roomFileId"
      :type="'file'"
      :section="section"
      :menuList="menuList(file)"
      :item="itemObj(file)"
      :hideDownload="isUploading"
      @onClickDownload="handleClickDownloadFile(file)"
      @onClickDelete="onClickDelete"
    />
  </div>  
</template>

<script>

import {mapActions, mapGetters, mapMutations} from "vuex";
import RoomChatForm from '@/components/Room/Chat/RoomChatForm'
import moment from "moment";

export default {
  name: 'RoomFile',
  components: {
    RoomChatForm
  },
  props: {
    section: {
      type: String,
      default: 'file',
    },
    isUploading: {
      type: Boolean,
      default: false,
    },
    files: Array,
  },
  computed: {
    ...mapGetters('file',[
       'roomFileList', 
    ]),
    ...mapGetters('user',[
      'hasEmceeRole', 
      'getRoomUser',
      'me',
    ]),
    isFileSection() {
      return this.section == 'file';
    },
    fileList(){
      return !this.files ? this.roomFileList : this.files
    },
    itemObj(){
      return file => {
        return {
          ...file,
          title: file.fileInfo.filename,
          filesize: this.getFormattedSize(file.fileInfo.filesize),
          expireDate: this.getFormedDate(file.expireDate),
          receiveDate: this.getReceiveDate(file.regDate),
          nickname: this.roomUserNickname(file.userId),
          isFile: true,
        }
      }
    },
    getReceiveDate(){
      return date => {
        if(!this.isFileSection)
          return this.getSimpleDate(date)
        else
          return this.getFullFormedDate(date)
      }
    },
    getFullFormedDate() {
      return date => {
        return !this.isUploading && moment(date).format('YYYY-MM-DD HH:mm');
      }
    },
    getSimpleDate() {
      return date => {
        return !this.isUploading && moment(date).format('HH:mm:ss');
      }
    },
    getFormedDate() {
      return date => {
        return !this.isUploading && moment(date).format('YYYY-MM-DD');
      }
    },
    getFormattedSize() {
      return filesize => {
        let size = filesize
        if(size > 1024*1024*1024){
          return `${Math.round(filesize/(1024*1024*1024)*100)/100}GB`;
        }else if(size > 1024*1024){
          return `${Math.round(filesize/(1024*1024)*100)/100}MB`;
        }else if(size > 1024){
          return `${Math.round(filesize/1024*100)/100}KB`;
        }else{
          return `${Math.round(filesize/100)/100}Byte`;
        }
      }
    },
    menuList() {
      return file => {
        if(this.isUploading) return
        
        let fileObj = {
          del: file.del,
          roomFileId: file.roomFileId,
        }
        return [{
          title: this.$t('chat.form.notice.post'),
          icon: 'notice',
          click: () => {
            this.handleClickNoticeFile(fileObj)
          }
        },]
      }
    },
    roomUserNickname: function() {
      return userId => {
        let ru;
        if(userId){
          ru = this.getRoomUser(userId);
          if(!ru){
            return this.$t('extendMenu.notice.leavedUser') // TODO 처리 필요
          }          
        }else{
          ru = this.me;
        }
        return ru.nickname.escapeXml();
      }
    },
  },
  methods:{
    ...mapActions('file', [
      'deleteRoomFile',
      'getRoomFileUrl',
      'uploadRoomFile',
      'getRoomFileBlob',
    ]),
    ...mapMutations('file', [
      'DELETE_ROOM_FILE',
      'UPDATE_FILE_DOWNLOAD_PROGRESS',
      'ADD_ROOM_FILE',
    ]),
    ...mapActions('notice', [
      'createNotice',
    ]),
    ...mapMutations('notice', [
      'DELETE_NOTICE_BY_FILE_ID',
    ]),
    async handleClickNoticeFile(file) {
      if(!this.hasEmceeRole())
        return;

      const {del} = file;
      if(del){
        this.$alert({
          text: '파일이 삭제되어 공지로 등록할 수 없습니다.',
          onlyOk: true,
        });
        return;
      }

      const noticeData = {
        msg: this.text,
        fixed: true,
        shareFileId: file.roomFileId
      }

      try{
        await this.createNotice(noticeData);
      } catch (e) {
        if(e.message.includes('[Duplicated]')){
          this.duplicatedNotice();
        }
      }
    },
    async handleClickDownloadFile(roomFile) {
      const {del} = roomFile;
      if(del){
        this.$alert({
          text: '파일이 삭제되어 다운로드 할 수 없습니다.',
          onlyOk: true,
        });
        return;
      }
      let blobUrl = await this.getFileBlobUrl(roomFile);
      this.runUrlTag({url: blobUrl, roomFile});
    },
    filenameFormat(roomFile) {
      return roomFile?.fileInfo?.filename;
    },
    runUrlTag({url, roomFile}){
      const a = document.createElement('a');
      try{
        a.href = url;
        a.download = this.filenameFormat(roomFile);
        a.target= '_blank'
        document.body.append(a);
        a.click();
      }finally{
        window.URL.revokeObjectURL(url);
        a.remove();
      }
    },
    async getFileBlobUrl(roomFile){
      this.UPDATE_FILE_DOWNLOAD_PROGRESS({roomFile, downloadState: 'preparing'});
      
      try {
        let url = await this.getRoomFileUrl(roomFile);
        let res = await fetch(url)
        if(!res.ok) {
          throw new Error(res.status)
        }
        
        const reader = res.body.getReader();
        const contentLength = res.headers.get('Content-Length');
  
        let receivedLength = 0;
        let chunks = [];
        let done = false;

        this.UPDATE_FILE_DOWNLOAD_PROGRESS({roomFile, downloadState: 'downloading'});
        while (!done) {
          const {done, value = 0} = await reader.read();

          if (done) {
            break;
          }

          chunks.push(value);
          receivedLength += value.length;

          this.UPDATE_FILE_DOWNLOAD_PROGRESS({roomFile, progress: Math.ceil(receivedLength / contentLength * 100)});
        }

        let Uint8Chunks = new Uint8Array(receivedLength), position = 0;
        for (let chunk of chunks) {
          Uint8Chunks.set(chunk, position);
          position += chunk.length;
        }

        let blobUrl = URL.createObjectURL(new Blob([Uint8Chunks]));
        return blobUrl;
      }catch (e) {
        this.$alert({
          text: this.$t('extendMenu.file.serverError'),
          onlyOk: true
        });
        throw e;
      }finally {
        this.UPDATE_FILE_DOWNLOAD_PROGRESS({roomFile, downloadState: 'completed'});
      }
    },
    duplicatedNotice (){
      this.$nextTick(() => {
        this.$alert({
          text: this.$t('extendMenu.file.exist'),
          onlyOk: true,
        });
      })
    },
    fileUploadFailed (e){
      this.$alert({
        text: this.$t('extendMenu.file.uploadFail', { '0' : e }),
        onlyOk: true,
      });
    },
    onClickDelete(file){
      this.$alert({
        text: this.$t('extendMenu.file.delete.text'),
        ok:()=>{
          this.deleteRoomFile({file})
        },
      })
    },
  },
}
</script>
